'use client';

/**
 * DemoTokenomicsCard — tokenomics footer for the demo canvas.
 *
 * Total tokens + stacked bar (prompt / thought / completion) + per-model
 * breakdown. Reads `TokenomicsRollup` from the scope-keyed
 * `tokenomics_rollup` BFF — for this canvas, scope=conversation,
 * scope_value=<simulation_id>. The same BFF serves other consumers
 * (tenant admin, capability ops, KlydeConfig tuning) with different
 * scopes; the card here doesn't care — it just renders a
 * `TokenomicsRollup` shape.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Coins } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TokenomicsRollup, ModelBreakdown } from './demo-test-run-types';


export function DemoTokenomicsCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data } = useQuery<TokenomicsRollup>({
    queryKey: ['tokenomics_rollup', 'conversation', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TokenomicsRollup>('tokenomics_rollup', {
        scope: 'conversation',
        scope_value: simulationId,
        group_by: ['ai_model'],
      });
      if (!res.success) throw new Error(res.error || 'tokenomics_rollup failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId) return null;
  if (!data || data.total_tokens === 0) return null;

  const { prompt_tokens: p, thought_tokens: t, completion_tokens: c, total_tokens: total } = data;
  const pPct = (p / total) * 100;
  const tPct = (t / total) * 100;
  const cPct = (c / total) * 100;

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <Coins className="w-4 h-4 text-muted-foreground" />
        <div className={cn(T.body, 'text-sm font-medium')}>Token economics</div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs ml-auto')}>
          {total.toLocaleString()} total · {data.inner_cv_count} call{data.inner_cv_count === 1 ? '' : 's'}
        </div>
      </div>

      <div className="flex h-2 rounded-full overflow-hidden bg-muted/40">
        <div style={{ width: `${pPct}%` }} className="bg-sky-500" title={`prompt ${p}`} />
        <div style={{ width: `${tPct}%` }} className="bg-violet-500" title={`thought ${t}`} />
        <div style={{ width: `${cPct}%` }} className="bg-emerald-500" title={`completion ${c}`} />
      </div>

      <div className="flex justify-between mt-2 text-xs">
        <Legend dot="bg-sky-500"      label="prompt"     value={p} />
        <Legend dot="bg-violet-500"   label="thought"    value={t} />
        <Legend dot="bg-emerald-500"  label="completion" value={c} />
        {data.cached_tokens > 0 && (
          <Legend dot="bg-muted-foreground/40" label="cached" value={data.cached_tokens} />
        )}
      </div>

      {data.by_model.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border/40 space-y-1">
          {data.by_model.map((m: ModelBreakdown) => (
            <div key={m.model} className="flex items-baseline gap-3 text-xs">
              <span className={cn(T.metadata, 'font-mono text-muted-foreground truncate flex-1')}>
                {m.model}
              </span>
              <span className={cn(T.metadata, 'text-muted-foreground')}>
                {m.calls} call{m.calls === 1 ? '' : 's'}
              </span>
              <span className={cn(T.body, 'font-medium text-xs tabular-nums')}>
                {m.total_tokens.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


interface LegendProps {
  dot: string;
  label: string;
  value: number;
}

function Legend({ dot, label, value }: LegendProps) {
  const T = useCardTypography();
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn('w-2 h-2 rounded-full', dot)} />
      <span className={cn(T.metadata, 'text-muted-foreground')}>{label}</span>
      <span className={cn(T.body, 'tabular-nums font-medium')}>{value.toLocaleString()}</span>
    </div>
  );
}
